import { useCallback, useEffect, useRef, useState } from 'react'

import { BaseItem, LoadItems } from './types'

type UseSearchOptions<T extends BaseItem> = {
  loadItems: LoadItems<T>
  pageSize: number
  debounce: number
}

export function useSearch<T extends BaseItem>({
  loadItems,
  pageSize,
  debounce,
}: UseSearchOptions<T>) {
  const loadItemsRef = useRef(loadItems)

  useEffect(() => {
    loadItemsRef.current = loadItems
  }, [loadItems])

  const [items, setItems] = useState<Array<T>>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error>()
  const [hasMore, setHasMore] = useState(false)

  const abortRef = useRef<AbortController | undefined>(undefined)
  const debounceRef = useRef<number | undefined>(undefined)

  // Последний запрос, по которому были (или будут) загружаться данные.
  const searchRef = useRef('')

  const initializedRef = useRef(false)
  const offsetRef = useRef(0)
  const initialItemsRef = useRef<Array<T> | undefined>(undefined)
  const initialHasMoreRef = useRef(false)

  const load = useCallback(
    async (reset: boolean, search: string) => {
      abortRef.current?.abort()

      const controller = new AbortController()
      abortRef.current = controller

      try {
        setLoading(true)
        setError(undefined)

        const offset = reset ? 0 : offsetRef.current

        const result = await loadItemsRef.current({
          search,
          offset,
          limit: pageSize,
          signal: controller.signal,
        })

        if (controller.signal.aborted) {
          return
        }

        searchRef.current = search
        offsetRef.current = offset + result.items.length

        if (reset && search === '') {
          initialItemsRef.current = result.items
          initialHasMoreRef.current = result.hasMore
        }

        setHasMore(result.hasMore)

        setItems((previous) => (reset ? result.items : [...previous, ...result.items]))
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e as Error)
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    },
    [pageSize],
  )

  const reload = useCallback(
    (search: string) => {
      initializedRef.current = true
      offsetRef.current = 0

      window.clearTimeout(debounceRef.current)

      if (search === '' && initialItemsRef.current) {
        searchRef.current = ''
        setItems(initialItemsRef.current)
        setHasMore(initialHasMoreRef.current)
        setError(undefined)
        offsetRef.current = initialItemsRef.current.length
        return
      }

      load(true, search)
    },
    [load],
  )

  const searchDebounced = useCallback(
    (search: string) => {
      if (!initializedRef.current) {
        return
      }
      window.clearTimeout(debounceRef.current)

      debounceRef.current = window.setTimeout(() => {
        offsetRef.current = 0
        load(true, search)
      }, debounce)
    },
    [debounce, load],
  )

  const loadMore = useCallback(() => {
    if (loading || !hasMore) {
      return
    }

    load(false, searchRef.current)
  }, [hasMore, load, loading])

  const reset = useCallback(() => {
    abortRef.current?.abort()

    window.clearTimeout(debounceRef.current)

    initializedRef.current = false
    offsetRef.current = 0
    searchRef.current = ''

    setItems([])
    setHasMore(false)
    setError(undefined)
    setLoading(false)
  }, [])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      window.clearTimeout(debounceRef.current)
    }
  }, [])

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    reload,
    searchDebounced,
    reset,
  }
}
